'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { motion, useAnimation } from 'framer-motion';

interface IconProps {
	isHovered?: boolean;
}

const faceVariants = {
	normal: {
		scale: 1,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 25,
		},
	},
	animate: {
		scale: 1.2,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 20,
		},
	},
};

const eyesVariants = {
	normal: {
		scale: 1,
		y: 0,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 25,
		},
	},
	animate: {
		scale: 1.2,
		y: -1,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 20,
			delay: 0.1,
		},
	},
};

const plusVariants = {
	normal: {
		rotate: 0,
		scale: 1,
		x: 0,
		y: 0,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 25,
		},
	},
	animate: {
		rotate: 90,
		scale: 1.2,
		x: 3,
		y: -3,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 20,
			delay: 0.1,
		},
	},
};

export function SmileIcon({ isHovered }: IconProps) {
	const controls = useAnimation();

	React.useEffect(() => {
		if (isHovered) {
			controls.start('animate');
		} else {
			controls.start('normal');
		}
	}, [isHovered, controls]);

	return (
		<div className='select-none p-1 flex items-center justify-center'>
			<svg
				xmlns='http://www.w3.org/2000/svg'
				width='50'
				height='50'
				viewBox='-4 -4 32 32'
				fill='none'
				stroke='currentColor'
				strokeWidth='2'
				strokeLinecap='round'
				strokeLinejoin='round'
				className={cn(
					'transition-colors text-white',
					isHovered && 'text-yellow-300'
				)}
			>
				<g>
					<motion.path
						d='M22 11v1a10 10 0 1 1-9-10'
						variants={faceVariants}
						initial='normal'
						animate={controls}
					/>
					<motion.path
						d='M8 14s1.5 2 4 2 4-2 4-2'
						variants={faceVariants}
						initial='normal'
						animate={controls}
					/>
					<motion.line
						x1='9'
						x2='9.01'
						y1='9'
						y2='9'
						variants={eyesVariants}
						initial='normal'
						animate={controls}
					/>
					<motion.line
						x1='15'
						x2='15.01'
						y1='9'
						y2='9'
						variants={eyesVariants}
						initial='normal'
						animate={controls}
					/>
					<motion.path
						d='M16 5h6'
						variants={plusVariants}
						initial='normal'
						animate={controls}
					/>
					<motion.path
						d='M19 2v6'
						variants={plusVariants}
						initial='normal'
						animate={controls}
					/>
				</g>
			</svg>
		</div>
	);
}
